import { useState, useEffect, useCallback, useRef } from "react";

/**
 * Custom hook for Server-Sent Events (SSE) with auto-reconnect
 * Includes suppression of postMessage cloning errors (browser extension conflicts)
 */
const useSSE = (url, token) => {
  const [data, setData] = useState(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 20;
  const baseReconnectDelay = 500;

  // Helper to check if error is a postMessage cloning error (usually from browser extensions)
  const isPostMessageError = (err) => {
    if (!err) return false;
    const msg = err.message || String(err);
    return msg.includes("postMessage") || msg.includes("could not be cloned");
  };

  const connect = useCallback(() => {
    if (!token) return;

    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Create EventSource with authorization header via URL param
    // Note: EventSource doesn't support custom headers directly,
    // so we pass the token as a query param
    const fullUrl = `${url}`;
    
    try {
      // For SSE with auth, we need to use fetch with streaming
      const controller = new AbortController();
      
      fetch(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        signal: controller.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          
          setConnected(true);
          setError(null);
          reconnectAttempts.current = 0;

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          const processStream = async () => {
            try {
              while (true) {
                const { done, value } = await reader.read();
                
                if (done) {
                  setConnected(false);
                  scheduleReconnect();
                  break;
                }

                buffer += decoder.decode(value, { stream: true });
                
                // Process complete SSE messages
                const lines = buffer.split("\n\n");
                buffer = lines.pop() || ""; // Keep incomplete message in buffer

                for (const line of lines) {
                  if (line.startsWith("data: ")) {
                    try {
                      const jsonData = JSON.parse(line.slice(6));
                      setData(jsonData);
                    } catch (e) {
                      // Silently ignore parse errors for SSE data
                    }
                  }
                }
              }
            } catch (err) {
              if (err.name !== "AbortError" && !isPostMessageError(err)) {
                console.warn("SSE stream reconnecting:", err?.message || err);
                setConnected(false);
                scheduleReconnect();
              }
            }
          };

          processStream();
        })
        .catch((err) => {
          if (err.name !== "AbortError") {
            // Suppress postMessage cloning errors (usually from browser extensions)
            if (isPostMessageError(err)) {
              // Silently ignore - these are browser extension conflicts
              return;
            }
            console.error("SSE connection error:", err);
            setError(err.message);
            setConnected(false);
            scheduleReconnect();
          }
        });

      // Store controller for cleanup
      eventSourceRef.current = { close: () => controller.abort() };
    } catch (err) {
      if (!isPostMessageError(err)) {
        console.error("SSE setup error:", err);
        setError(err.message);
        scheduleReconnect();
      }
    }
  }, [url, token]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      console.error("Max reconnect attempts reached");
      setError("Connection lost. Please refresh the page.");
      return;
    }

    const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts.current);
    reconnectAttempts.current += 1;

    console.log(`Scheduling reconnect in ${delay}ms (attempt ${reconnectAttempts.current})`);

    reconnectTimeoutRef.current = setTimeout(() => {
      connect();
    }, delay);
  }, [connect]);

  // Connect on mount and when dependencies change
  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  // Manual reconnect function
  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  return { data, connected, error, reconnect };
};

export default useSSE;
